import {PADDLE_CONFIG, SUBSCRIPTION_FAILED_STATES} from '/src/const';
import type {TBillingCycle} from './pricing.types';
import type {ILocalizedPlanPrice} from './PricingPlans2.prices';

// The currency of the plan prices in pricing.constants, shown while Paddle prices load.
const FALLBACK_CURRENCY_CODE = 'USD';

type TCheckoutBillingCycle = ReturnType<typeof getCheckoutBillingCycle>;
type TCheckoutOfferQuery = Record<string, string | null | (string | null)[] | undefined>;
type TPaidPlan = 'hobby' | 'plus';
type TSubscriptionPlan = 'HOBBY' | 'PLUS' | string | null | undefined;
type TActiveSubscriptionChangeAction = 'current' | 'self_serve' | 'manual';
type TPricingSubscription = {
    status?: string | null;
    ends_at?: string | null;
};

function getPaidPlanRank(plan: TSubscriptionPlan | TPaidPlan): number | undefined {
    const normalized = plan?.toLowerCase();
    if (normalized === 'hobby') return 1;
    if (normalized === 'plus') return 2;
    return undefined;
}

export function getCheckoutBillingCycle(billingCycle: TBillingCycle): 'week' | 'month' | 'annual' | 'lifetime' {
    if (billingCycle === 'weekly') return 'week';
    if (billingCycle === 'monthly') return 'month';
    if (billingCycle === 'lifetime') return 'lifetime';
    return 'annual';
}

function isCanceledStatus(status?: string | null): boolean {
    return status === 'canceled' || status === 'cancelled';
}

function hasFutureEndsAt(endsAt?: string | null, now = new Date()): boolean {
    if (!endsAt) return false;
    const endDate = new Date(endsAt);
    return Number.isFinite(endDate.getTime()) && endDate.getTime() > now.getTime();
}

export function isPricingSubscriptionActive(
    subscription: TPricingSubscription | null | undefined,
    now = new Date(),
): boolean {
    if (!subscription) return false;
    if (SUBSCRIPTION_FAILED_STATES.includes(subscription.status ?? '')) return false;
    if (isCanceledStatus(subscription.status)) return hasFutureEndsAt(subscription.ends_at, now);
    return true;
}

export function stripZeroCents(value: string): string {
    return value.replace(/[.,]00$/, '');
}

export function getPaddlePriceId(plan: 'hobby' | 'plus', cycle: TBillingCycle): string | undefined {
    if (cycle === 'weekly') {
        return plan === 'plus' ? PADDLE_CONFIG.prices.plus.week : undefined;
    }
    if (cycle === 'lifetime') {
        return plan === 'plus' ? PADDLE_CONFIG.prices.plus.lifetime : undefined;
    }
    const checkoutCycle = getCheckoutBillingCycle(cycle);
    const planPrices = PADDLE_CONFIG.prices[plan] as Partial<Record<TCheckoutBillingCycle, string>>;
    return planPrices[checkoutCycle];
}

export function getLocalizedPrice(
    plan: 'hobby' | 'plus',
    cycle: TBillingCycle,
    localizedPrices: Record<string, ILocalizedPlanPrice>,
): ILocalizedPlanPrice | undefined {
    const priceId = getPaddlePriceId(plan, cycle);
    return priceId ? localizedPrices[priceId] : undefined;
}

/** `locale` is the page language; without it the browser language decides the number format. */
export function formatMonthlyEquivalent(yearlyPrice: ILocalizedPlanPrice | undefined, locale?: string): string {
    if (yearlyPrice) {
        const num = Number(yearlyPrice.rawTotal) / 100;
        if (!isNaN(num)) {
            return formatLocalizedCurrency(num / 12, yearlyPrice.currencyCode, locale);
        }
    }
    return '-';
}

/**
 * An amount in `currencyCode` without zero cents (`$20`, `20 €`). `locale` is the page language;
 * without it the browser language decides the number format.
 */
function formatLocalizedCurrency(amount: number, currencyCode: string, locale?: string): string {
    const parts = new Intl.NumberFormat(locale, {style: 'currency', currency: currencyCode}).formatToParts(amount);
    const hasZeroCents = parts.some((part) => part.type === 'fraction' && /^0+$/.test(part.value));
    return parts
        .filter((part) => !(hasZeroCents && (part.type === 'decimal' || part.type === 'fraction')))
        .map((part) => part.value)
        .join('');
}

/** A plan price from pricing.constants, shown before Paddle prices arrive. */
export function formatPlanPrice(amount: number, locale?: string): string {
    return formatLocalizedCurrency(amount, FALLBACK_CURRENCY_CODE, locale);
}

export function formatYearlySavings(
    monthlyPrice: ILocalizedPlanPrice | undefined,
    yearlyPrice: ILocalizedPlanPrice | undefined,
    locale?: string,
): string | undefined {
    if (!monthlyPrice || !yearlyPrice) return undefined;

    const monthlyTotal = Number(monthlyPrice.rawTotal) / 100;
    const yearlyTotal = Number(yearlyPrice.rawTotal) / 100;
    if (!Number.isFinite(monthlyTotal) || !Number.isFinite(yearlyTotal)) return undefined;

    const savings = monthlyTotal * 12 - yearlyTotal;
    if (savings <= 0) return undefined;

    return formatLocalizedCurrency(Math.trunc(savings), yearlyPrice.currencyCode, locale);
}

export function formatPlanYearlySavings(
    monthlyPrice: number | 'FREE',
    yearlyPrice: number | 'FREE',
    locale?: string,
): string | undefined {
    if (typeof monthlyPrice !== 'number' || typeof yearlyPrice !== 'number') return undefined;

    const savings = monthlyPrice * 12 - yearlyPrice;
    if (savings <= 0) return undefined;

    return formatPlanPrice(Math.trunc(savings), locale);
}

export function loginHash(plan: string, billingCycle: TBillingCycle): string {
    if (billingCycle === 'lifetime') return `#buy_${plan}_lifetime`;
    if (billingCycle === 'yearly') return `#buy_${plan}_annual`;
    if (billingCycle === 'weekly') return `#buy_${plan}_week`;
    return `#buy_${plan}`;
}

export function getCheckoutOfferCode(query: TCheckoutOfferQuery): string | undefined {
    const rawOffer = Array.isArray(query.offer) ? query.offer[0] : query.offer;
    if (!rawOffer) return undefined;

    const normalizedOffer = rawOffer.trim().toUpperCase();
    if (!/^[A-Z0-9_-]{1,64}$/.test(normalizedOffer)) return undefined;

    return normalizedOffer;
}

export function buildLoginCheckoutPath(
    plan: 'hobby' | 'plus',
    billingCycle: TBillingCycle,
    offerCode?: string,
): string {
    const query = offerCode ? `?offer=${encodeURIComponent(offerCode)}` : '';
    return `/login${query}${loginHash(plan, billingCycle)}`;
}

export function buildBuyNowCheckoutPath(isBuyNow: string, offerCode?: string): string {
    const query = offerCode ? `?offer=${encodeURIComponent(offerCode)}` : '';
    return `/buy_${isBuyNow}${query}`;
}

export function buildSubscriptionChangePath(
    plan: TPaidPlan,
    billingCycle: Exclude<TBillingCycle, 'weekly' | 'lifetime'>,
    offerCode?: string,
): string {
    const params = new URLSearchParams({plan, cycle: billingCycle});
    const normalizedOfferCode = getCheckoutOfferCode({offer: offerCode});
    if (normalizedOfferCode) {
        params.set('offer', normalizedOfferCode);
    }
    return `/upgrade?${params.toString()}`;
}

export function getActiveSubscriptionChangeAction(
    currentPlan: TSubscriptionPlan,
    currentBillingCycle: TBillingCycle | 'unknown',
    targetPlan: TPaidPlan,
    targetBillingCycle: TBillingCycle,
): TActiveSubscriptionChangeAction {
    if (currentPlan?.toLowerCase() === targetPlan && currentBillingCycle === targetBillingCycle) return 'current';
    if (targetBillingCycle === 'weekly' || targetBillingCycle === 'lifetime') return 'manual';
    if (currentBillingCycle === 'yearly' && targetBillingCycle === 'monthly') return 'manual';
    const currentPlanRank = getPaidPlanRank(currentPlan);
    const targetPlanRank = getPaidPlanRank(targetPlan);
    if (currentPlanRank && targetPlanRank && targetPlanRank < currentPlanRank) return 'manual';
    return 'self_serve';
}
